import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  OwnSaveTokens,
  type MatchVerdictDto,
  type MatchCandidateLocator,
  type MatchConfirmPreviewDto,
  type ProspectBookDto,
  type ProspectBookQuery,
  type ProspectBookCountsDto,
  type ProspectBookRowDto,
  type ProspectBookSummaryDto,
  type ProspectDetailDto,
  type ProspectDto,
  type ProspectWorkLineDto,
  type PossibleMatchesDto,
  type RecordMatchVerdictRequest,
  type UpdateProspectNotesRequest,
  type UpdateProspectRequest,
  type WorkLineFieldDto,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiVoid, type ApiErrorBody } from './apiFetch';
import { bookKeyQuery, bookQueryOfKey, queryKeys } from '../../lib/queryKeys';
import { candidateKey } from '../../lib/fundraising/matchVerdict';
import { useCampaignStore } from '../../stores';
import { invalidateAroundBooks, isBook, markBooksStale, patchBookRows, patchBookSheets, patchPossibleMatches } from './prospectBookCache';

const PROSPECTS_PATH = '/api/fundraising/prospects';

/** How long typing must pause before the book is searched — each search reads the whole book. */
const BOOK_SEARCH_PAUSE_MS = 250;

/** The search once typing pauses; clearing it counts at once. */
function useSettledSearch(term: string) {
  const [settled, setSettled] = useState(term);
  useEffect(() => {
    if (term === '') {
      // So the next search starts from nothing, not from the one just cleared.
      setSettled('');
      return;
    }
    const timer = setTimeout(() => setSettled(term), BOOK_SEARCH_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [term]);
  return term === '' ? '' : settled;
}

/** The book's filters as its query string — the same for the book and its summary. */
function bookParams(read: ProspectBookQuery): URLSearchParams {
  const { activity, match, sheetId, search, listId, state, city, zip, minAsk, maxAsk, timeZone, sort, dir } = read;
  const params = new URLSearchParams();
  if (activity) params.set('activity', activity);
  if (match) params.set('match', match);
  if (sheetId) params.set('sheet', sheetId);
  if (search) params.set('search', search);
  if (listId) params.set('list', listId);
  if (state) params.set('state', state);
  if (city) params.set('city', city);
  if (zip) params.set('zip', zip);
  if (minAsk != null) params.set('minAsk', String(minAsk));
  if (maxAsk != null) params.set('maxAsk', String(maxAsk));
  if (timeZone) params.set('timeZone', timeZone);
  if (sort) params.set('sort', sort);
  if (dir) params.set('dir', dir);
  return params;
}

/** The book's request for a query whose search has settled. */
async function fetchBook(read: ProspectBookQuery): Promise<ProspectBookDto> {
  const query = bookParams(read).toString();
  return apiJson<ProspectBookDto>(`${PROSPECTS_PATH}/book${query ? `?${query}` : ''}`, { error: 'Failed to fetch the prospects' });
}

/** The summary's request: the book's filters, and whether to send back its order. */
async function fetchBookSummary(read: ProspectBookQuery, withIds: boolean): Promise<ProspectBookSummaryDto> {
  const params = bookParams(read);
  if (withIds) params.set('withIds', 'true');
  const query = params.toString();
  return apiJson<ProspectBookSummaryDto>(`${PROSPECTS_PATH}/book/summary${query ? `?${query}` : ''}`, {
    error: 'Failed to fetch the prospects',
  });
}

/** The book's filters with the search, city and ZIP taken once typing pauses. */
function useSettledBookQuery(query: ProspectBookQuery): ProspectBookQuery {
  const search = useSettledSearch((query.search ?? '').trim());
  const city = useSettledSearch((query.city ?? '').trim());
  const zip = useSettledSearch((query.zip ?? '').trim());
  return { ...query, search, city, zip };
}

/**
 * The Prospects book (Fundraising: Manage): every Prospect with their match status, Ask and federal
 * giving, largest Ask first, plus the chip counts. The activity filter, the Match filter, a call book, an
 * uploaded list, a place, a time zone, an Ask range and the search all narrow it on the server, together; a call book
 * shows its people in its order, with its progress. A column sort is the server's too, over the whole filtered
 * set before its cap — the browser never sorts the rows. The search, city and ZIP wait for typing to pause.
 * The previous book stays on screen while the next one loads (`isPlaceholderData` says it's stale).
 */
export function useProspectBook(query: ProspectBookQuery = {}) {
  const { selectedCampaign } = useCampaignStore();
  const settled = useSettledBookQuery(query);
  return useQuery({
    queryKey: queryKeys.fundraising.prospectBook(settled),
    queryFn: () => fetchBook(settled),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    placeholderData: (previous) => previous,
  });
}

/**
 * The book's figures without its rows (Fundraising: Manage): the same total and chip counts the book reads for the
 * same filters, every call book's name, size and token, and — `withIds` — who the book shows, in its order, as ids.
 * For whatever needs the book's shape but not its rows — a person's page stepping through it, the Call books tab's
 * tokens — so they never read the whole book. Any change to a person or a call book re-reads it (its key sits under
 * the Prospects prefix).
 */
export function useProspectBookSummary(query: ProspectBookQuery = {}, { withIds = false }: { withIds?: boolean } = {}) {
  const { selectedCampaign } = useCampaignStore();
  const settled = useSettledBookQuery(query);
  return useQuery({
    queryKey: queryKeys.fundraising.prospectBookSummary(settled, withIds),
    queryFn: () => fetchBookSummary(settled, withIds),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    placeholderData: (previous) => previous,
  });
}

/**
 * How long a person's page stands before opening them reads it again. It lets a read started early — a hover over
 * their row, a step's neighbour — serve the open without a second request. Any change to a person, a verdict or a
 * call book marks it stale at once (its key sits under the Prospects prefix), so this never hides an edit.
 */
const PROSPECT_DETAIL_FRESH_MS = 30_000;

/** A person's page as a query: the one key, read and freshness both opening and prefetching use. */
const prospectDetailQuery = (prospectId: string) => ({
  queryKey: queryKeys.fundraising.prospectDetail(prospectId),
  queryFn: () => apiJson<ProspectDetailDto>(`${PROSPECTS_PATH}/${prospectId}/detail`, { error: 'Failed to fetch this person' }),
  staleTime: PROSPECT_DETAIL_FRESH_MS,
});

/**
 * One Prospect's detail page (Fundraising: Manage): their book row, open commitment, call books, each
 * accepted federal record with who decided it, and the timeline. Any change to a person, a verdict or a
 * call book refreshes it (its key sits under the Prospects prefix).
 */
export function useProspectDetail(prospectId: string) {
  const { selectedCampaign } = useCampaignStore();
  return useQuery({
    ...prospectDetailQuery(prospectId),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

/**
 * Start reading a person's page before they're opened, so the open shows it at once. A read still fresh is
 * reused, never repeated, so calling it often costs nothing. The same function every render.
 */
export function usePrefetchProspectDetail() {
  const queryClient = useQueryClient();
  const { selectedCampaign } = useCampaignStore();
  const enabled = authService.isAuthenticated() && !!selectedCampaign;
  return useCallback(
    (prospectId: string) => (enabled ? queryClient.prefetchQuery(prospectDetailQuery(prospectId)) : Promise.resolve()),
    [queryClient, enabled],
  );
}

/**
 * Match review's read (Fundraising: Manage): everyone with a candidate still waiting on "Is this them?", with the
 * candidates in full — the one read that carries them; a book row carries only how many and the largest gift. Any
 * change to a person re-reads it (its key sits under the Prospects prefix); an answer patches it instead.
 */
export function usePossibleMatches() {
  const { selectedCampaign } = useCampaignStore();
  return useQuery({
    queryKey: queryKeys.fundraising.possibleMatches(),
    queryFn: () =>
      apiJson<PossibleMatchesDto>(`${PROSPECTS_PATH}/book/possible-matches`, { error: 'Failed to fetch the possible matches' }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

/** How long the tab counts stand before a page change re-reads them. Any Fundraising change refreshes them sooner. */
const TAB_COUNTS_FRESH_MS = 30_000;

/** What the Fundraising tabs count: the book's chip counts and how many call books there are. */
interface FundraisingTabCounts {
  counts: ProspectBookCountsDto;
  callBooks: number;
}

const tabCountsOf = (read: { counts: ProspectBookCountsDto; sheets: unknown[] }): FundraisingTabCounts => ({
  counts: read.counts,
  callBooks: read.sheets.length,
});

/** Whether a book read counts the whole campaign: no filter narrows it (its order moves no count). */
function countsWholeCampaign(query: ProspectBookQuery): boolean {
  const { sort: _sort, dir: _dir, ...filters } = bookKeyQuery(query);
  const { sort: _s, dir: _d, ...whole } = bookKeyQuery({});
  return (Object.keys(whole) as (keyof typeof whole)[]).every((key) => filters[key] === whole[key]);
}

/**
 * Where a cached book read stands: not read yet, read and current, marked stale by a change the patches can't
 * count (`markBooksStale`) — being read again (`reloading`, which brings fresh counts of its own) or not — or
 * failed. Re-renders only when that changes.
 */
function useBookReadState(key: readonly unknown[]): 'loading' | 'current' | 'reloading' | 'stale' | 'failed' {
  const queryClient = useQueryClient();
  const subscribe = useCallback((onChange: () => void) => queryClient.getQueryCache().subscribe(onChange), [queryClient]);
  return useSyncExternalStore(subscribe, () => {
    const state = queryClient.getQueryState(key);
    if (state?.status === 'error') return 'failed';
    if (state?.data === undefined) return 'loading';
    if (!state.isInvalidated) return 'current';
    return state.fetchStatus === 'fetching' ? 'reloading' : 'stale';
  });
}

/**
 * The counts the Fundraising tabs show — everyone, the call books, the possible matches and the open
 * commitments — for the whole campaign, never reading the whole book for them. `book` is the book the page is
 * showing, if any: when it's the whole campaign's (the Prospects tab with nothing filtered, in any order) its own
 * counts are the tab counts, so the page reads nothing more; once a change marks its counts out of date, and on
 * every other page, they come from the unfiltered book's summary — the figures without the rows. Moving between
 * tabs reuses it; any change to a person or a call book re-reads it at once.
 */
export function useFundraisingTabCounts(book?: ProspectBookQuery) {
  const { selectedCampaign } = useCampaignStore();
  const settled = useSettledBookQuery(book ?? {});
  const bookKey = queryKeys.fundraising.prospectBook(settled);
  const bookState = useBookReadState(bookKey);
  // The whole book is on its way, read and current, or being read again: it brings the counts, and the summary isn't
  // asked as well.
  const bookBringsCounts =
    book !== undefined && countsWholeCampaign(settled) && (bookState === 'loading' || bookState === 'current' || bookState === 'reloading');
  // Never reads: watches the page's own book read (useProspectBook reads it). While another order of the whole
  // book loads, the last one's counts stand — never a filtered book's, nor ones a change marked out of date.
  const fromBook = useQuery({
    queryKey: bookKey,
    queryFn: () => fetchBook(settled),
    enabled: false,
    select: tabCountsOf,
    placeholderData: (previous, previousQuery) => {
      const previousBook = previousQuery && bookQueryOfKey(previousQuery.queryKey);
      return previousBook && countsWholeCampaign(previousBook) && !previousQuery.state.isInvalidated ? previous : undefined;
    },
  });
  const summary = useQuery({
    queryKey: queryKeys.fundraising.prospectBookSummary({}, false),
    queryFn: () => fetchBookSummary({}, false),
    enabled: authService.isAuthenticated() && !!selectedCampaign && !bookBringsCounts,
    staleTime: TAB_COUNTS_FRESH_MS,
    select: tabCountsOf,
  });
  // While the whole book loads with nothing to stand in, whatever summary is cached shows; while a book whose counts
  // a change put out of date is read again, the summary read since stands until it lands.
  const bookStale = bookState === 'reloading' && summary.data !== undefined;
  return bookBringsCounts && fromBook.data !== undefined && !bookStale ? fromBook : summary;
}

/**
 * "Suggested ask becomes": what a Prospect's Ask would be if the chosen candidates were confirmed, and
 * what they add — worked out on the server by the rule the book uses, so the web never sizes an Ask.
 * Read-only (a POST only to carry the chosen locators). Nothing chosen asks nothing.
 */
export function useMatchConfirmPreview(prospectId: string, candidates: MatchCandidateLocator[]) {
  const { selectedCampaign } = useCampaignStore();
  const chosen = candidates.map((c) => ({
    givenName: c.givenName,
    lastCore: c.lastCore,
    suffix: c.suffix,
    streetFolded: c.streetFolded,
    zip5: c.zip5,
  }));
  return useQuery({
    queryKey: queryKeys.fundraising.matchConfirmPreview(prospectId, chosen.map(candidateKey).sort()),
    queryFn: () =>
      apiJson<MatchConfirmPreviewDto>(`${PROSPECTS_PATH}/${prospectId}/match-verdicts/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        json: { candidates: chosen },
        error: "Couldn't work out the suggested ask",
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign && chosen.length > 0,
    placeholderData: (previous) => previous,
  });
}

/** Every cached read under the Prospects prefix: a plain list, the book, or one person's detail page. */
type ProspectsCache = ProspectDto[] | ProspectBookDto | ProspectDetailDto | undefined;
type ProspectListsSnapshot = [readonly unknown[], ProspectsCache][];


const isDetail = (data: ProspectsCache): data is ProspectDetailDto =>
  !!data && !Array.isArray(data) && 'row' in data && !!data.row;

/**
 * A row's work line after its Prospect changed: their own value shows as it is, and a field still blank keeps
 * what the server read from their FEC record (a newly blank one shows nothing) until the settle refetch.
 */
function reconcileWorkLine(workLine: ProspectWorkLineDto, prospect: ProspectDto): ProspectWorkLineDto {
  const field = (own: string | null, shown: WorkLineFieldDto): WorkLineFieldDto =>
    own?.trim() ? { value: own, fromFec: false } : shown.fromFec ? shown : { value: null, fromFec: false };
  return { employer: field(prospect.employer, workLine.employer), occupation: field(prospect.occupation, workLine.occupation) };
}

/** Change one Prospect in a book row, keeping the work line it shows in step. */
function patchRow(row: ProspectBookRowDto, patch: (row: ProspectDto) => ProspectDto): ProspectBookRowDto {
  const prospect = patch(row.prospect);
  return { ...row, prospect, workLine: reconcileWorkLine(row.workLine, prospect) };
}

/** Change one Prospect wherever a cached read holds them, whatever its shape. */
function patchProspect(data: ProspectsCache, id: string, patch: (row: ProspectDto) => ProspectDto): ProspectsCache {
  if (Array.isArray(data)) return data.map((row) => (row.id === id ? patch(row) : row));
  if (isBook(data)) {
    return { ...data, rows: data.rows.map((row) => (row.prospect.id === id ? patchRow(row, patch) : row)) };
  }
  if (isDetail(data) && data.row.prospect.id === id) {
    return { ...data, row: patchRow(data.row, patch) };
  }
  return data;
}

/** Take one Prospect out of every cached read; the settle refetch brings the counts along. */
function dropProspect(data: ProspectsCache, id: string): ProspectsCache {
  if (Array.isArray(data)) return data.filter((row) => row.id !== id);
  if (isBook(data)) return { ...data, rows: data.rows.filter((row) => row.prospect.id !== id) };
  return data;
}

/**
 * This tab's own Prospect saves, shared by every edit that moves a Prospect's token (the details and the
 * note), so an edit built before another kind of save by this tab steps forward to that save's token
 * instead of false-conflicting. One per query client — one per tab in the app, one per test.
 */
const ownSavesByClient = new WeakMap<QueryClient, OwnSaveTokens>();
function ownProspectSaves(queryClient: QueryClient): OwnSaveTokens {
  let saves = ownSavesByClient.get(queryClient);
  if (!saves) {
    saves = new OwnSaveTokens();
    ownSavesByClient.set(queryClient, saves);
  }
  return saves;
}

/** A note as the server stores it: trimmed, and blank clears it. */
const cleanNote = (notes: string | null) => notes?.trim() || null;

/** The server's reason a Prospect change was refused (a JSON `null` body reaches here as null), or the fallback. */
const refusal = (fallback: string) => (error: ApiErrorBody | null) => new Error(error?.error ?? error?.message ?? fallback);

/**
 * A Fundraiser's correction of one Prospect's details, following the recipe for optimistic mutations
 * that carry a concurrency token (apps/web/CLAUDE.md): patch the row in every cached list, roll back on
 * failure, write the server's row — and its fresh token — back on success, and refetch on settle so
 * the composed name and anything derived from the details reconcile. Edits run one at a time (one
 * scope — the hook serves every row and a Fundraiser corrects one person at a time). Each sends the
 * token its form was built from, stepped forward only through this tab's own saves, so a quick second
 * edit doesn't false-conflict while another user's edit still makes a stale form a 409 — which rejects
 * with the server's message.
 */
export function useUpdateProspect() {
  const queryClient = useQueryClient();
  const listsKey = queryKeys.fundraising.prospects();
  const ownSaves = ownProspectSaves(queryClient);

  const patchRows = (id: string, patch: (row: ProspectDto) => ProspectDto) =>
    queryClient.setQueriesData<ProspectsCache>({ queryKey: listsKey }, (data) => patchProspect(data, id, patch));

  return useMutation({
    scope: { id: 'fundraising-prospect' },
    mutationFn: async ({ id, request }: { id: string; request: UpdateProspectRequest }) => {
      const sent = ownSaves.tokenFor(id, request.concurrencyVersion);
      const body = { ...request, concurrencyVersion: sent };
      const saved = await apiJson<ProspectDto>(`${PROSPECTS_PATH}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        json: body,
        error: refusal("Couldn't save the details. Try again."),
      });
      ownSaves.record(id, sent, saved.concurrencyVersion);
      return saved;
    },
    onMutate: async ({ id, request }) => {
      await queryClient.cancelQueries({ queryKey: listsKey });
      const snapshot: ProspectListsSnapshot = queryClient.getQueriesData<ProspectsCache>({ queryKey: listsKey });
      // The token stays the cached one until the server returns the next.
      patchRows(id, (row) => ({ ...row, ...request, concurrencyVersion: row.concurrencyVersion }));
      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      for (const [key, rows] of context?.snapshot ?? []) queryClient.setQueryData(key, rows);
    },
    onSuccess: (saved) => patchRows(saved.id, () => saved),
    onSettled: (_saved, _error, { id }) => {
      // Never the book: the row is patched. What the details move beyond it — the time zone, the work line's
      // FEC fill, how each candidate compares — comes from their page, which is on screen while they're
      // corrected and is read again here anyway; its row is their book row.
      void invalidateAroundBooks(queryClient).then(() => {
        const page = queryClient.getQueryData<ProspectDetailDto>(queryKeys.fundraising.prospectDetail(id));
        if (page) patchBookRows(queryClient, [page.row]);
      });
      // A cleared phone moves the chip counts, which only a fresh read of a book can say.
      void markBooksStale(queryClient);
      // A call book's lines carry their name.
      void queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.callSheets() });
    },
  });
}

/**
 * Writing a Prospect's person-wide note (Fundraising: Do) from the book's row or their page, following the
 * recipe for optimistic mutations that carry a concurrency token (apps/web/CLAUDE.md): the note shows at
 * once in every cached read, goes back if the save is refused, and the server's row — with its fresh
 * token — is written back on success. It queues with the details edit (one scope, one token to step) and
 * shares its own-save tokens, so a details form opened before a note save doesn't false-conflict. A blank
 * note clears it; a refusal rejects with the server's message.
 */
export function useUpdateProspectNotes() {
  const queryClient = useQueryClient();
  const listsKey = queryKeys.fundraising.prospects();
  const ownSaves = ownProspectSaves(queryClient);

  const patchRows = (id: string, patch: (row: ProspectDto) => ProspectDto) =>
    queryClient.setQueriesData<ProspectsCache>({ queryKey: listsKey }, (data) => patchProspect(data, id, patch));

  return useMutation({
    scope: { id: 'fundraising-prospect' },
    mutationFn: async ({ id, notes, concurrencyVersion }: { id: string } & UpdateProspectNotesRequest) => {
      const sent = ownSaves.tokenFor(id, concurrencyVersion);
      const body: UpdateProspectNotesRequest = { notes: cleanNote(notes), concurrencyVersion: sent };
      const saved = await apiJson<ProspectDto>(`${PROSPECTS_PATH}/${id}/notes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        json: body,
        error: refusal("Couldn't save the note. Try again."),
      });
      ownSaves.record(id, sent, saved.concurrencyVersion);
      return saved;
    },
    onMutate: async ({ id, notes }) => {
      await queryClient.cancelQueries({ queryKey: listsKey });
      const snapshot: ProspectListsSnapshot = queryClient.getQueriesData<ProspectsCache>({ queryKey: listsKey });
      // The token stays the cached one until the server returns the next.
      patchRows(id, (row) => ({ ...row, notes: cleanNote(notes) }));
      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      for (const [key, rows] of context?.snapshot ?? []) queryClient.setQueryData(key, rows);
    },
    onSuccess: (saved) => patchRows(saved.id, () => saved),
    // The note is all that changed and the row already holds it: never the book, nor the counts.
    onSettled: () => invalidateAroundBooks(queryClient, { summaries: false }),
  });
}

/**
 * A Fundraiser's removal of one Prospect nobody has called. Not optimistic: the server refuses anyone
 * with call history, so the row leaves the cached lists only once the removal lands. A refusal (call
 * history, or a stale token) rejects with the server's message, which names Do Not Call when that is
 * the way forward.
 */
export function useRemoveProspect() {
  const queryClient = useQueryClient();
  const listsKey = queryKeys.fundraising.prospects();

  return useMutation({
    scope: { id: 'fundraising-prospect' },
    mutationFn: async ({ id, concurrencyVersion }: { id: string; concurrencyVersion: number }) => {
      await apiVoid(`${PROSPECTS_PATH}/${id}?concurrencyVersion=${concurrencyVersion}`, {
        method: 'DELETE',
        error: refusal("Couldn't remove this person. Try again."),
      });
      return id;
    },
    onSuccess: (id) => {
      // Their detail page goes with them, so the settle refetch never asks for someone who's gone.
      queryClient.removeQueries({ queryKey: queryKeys.fundraising.prospectDetail(id) });
      queryClient.setQueriesData<ProspectsCache>({ queryKey: listsKey }, (data) => dropProspect(data, id));
      // Off every call book too — the book's own copy of who's on each.
      patchBookSheets(queryClient, (sheets) =>
        sheets.map((sheet) =>
          sheet.prospectIds.includes(id)
            ? { ...sheet, prospectIds: sheet.prospectIds.filter((p) => p !== id), size: Math.max(0, sheet.size - 1) }
            : sheet,
        ),
      );
    },
    // Never the book: the row is gone from it. The counts move, which the summary re-reads now and a book on its
    // next read. A call book's lines (and the Call books tab's sizes) go with them too.
    onSettled: () =>
      Promise.all([
        invalidateAroundBooks(queryClient),
        markBooksStale(queryClient),
        queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.callSheets() }),
      ]),
  });
}

const rereadPossibleMatches = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.possibleMatches() });

/**
 * A Fundraiser's answer to "Is this them?" for one candidate of one Prospect. Not optimistic: a yes moves
 * the person's giving and Ask, which only the server works out — the answer returns their row as the book now
 * reads it, and that row replaces theirs in every cached book, which is never read again for it, and patches Match
 * review's list (re-read only when the row says more changed than the answer). Answers run
 * one at a time. A refusal (someone answered first, or the candidate left on a re-run) rejects with the
 * server's message.
 */
export function useRecordMatchVerdict() {
  const queryClient = useQueryClient();

  return useMutation({
    scope: { id: 'fundraising-match-verdict' },
    mutationFn: ({ prospectId, request }: { prospectId: string; request: RecordMatchVerdictRequest }) =>
      apiJson<MatchVerdictDto>(`${PROSPECTS_PATH}/${prospectId}/match-verdicts`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        json: request,
        error: refusal("Couldn't save your answer. Try again."),
      }),
    onSuccess: async (verdict, { prospectId, request }) => {
      if (verdict.row) patchBookRows(queryClient, [verdict.row]);
      // Match review's list is patched too — reviewing a list re-reading it after every answer is a re-read per
      // person. A re-read already under way may have been answered before this save, and landing after the patch
      // would put the answered record back: it's dropped, and asked again once the patch is in.
      const key = queryKeys.fundraising.possibleMatches();
      const wasReading = queryClient.isFetching({ queryKey: key, exact: true }) > 0;
      await queryClient.cancelQueries({ queryKey: key, exact: true });
      if (!patchPossibleMatches(queryClient, prospectId, request, verdict.row) || wasReading) void rereadPossibleMatches(queryClient);
    },
    // A refusal means someone answered first or the candidate left on a re-run: the list is the server's to say.
    onError: () => void rereadPossibleMatches(queryClient),
    onSettled: () => {
      // Their page, the tab counts and the preview — the small reads. A book's chip counts follow on its next read.
      void invalidateAroundBooks(queryClient, { possibleMatches: false });
      void markBooksStale(queryClient);
    },
  });
}
